import React, { useRef, useState } from "react";
import Papa from "papaparse";
import ModalSMS from "../components/ModalSMS";
import { Message } from "semantic-ui-react";
import CSVSampleDownloadButton from "../../OutboundCampaign/CSVSampleDownloadButton";
import { normalizePhoneNumber } from "../../OutboundCampaign/scheduleUtils";

// Match the existing Outbound Voice campaign CSV sample (CampaignForm.js)
const SAMPLE_HEADERS = ["Name", "Phone"];
const SAMPLE_DATA = [
  ["John Doe", "(123)456-7890"],
  ["Jane Smith", "(098)765-4321"],
  ["Mike Johnson", "(112)233-4455"],
  ["Sarah Wilson", "(555)123-4567"],
];

const MANUAL_NAME_MAX_LENGTH = 50;
const MANUAL_NAME_MIN_LENGTH = 2;

// Match Outbound Voice campaign manual / CSV name rules (CampaignForm.js)
const nameRegex = /^(?!\d+$)(?!.*[.\-'\s]{2})[\p{L}0-9][\p{L}0-9.\-'\s]{0,48}[\p{L}0-9.]$/u;

function validateCustomerName(name) {
  const errors = [];
  const trimmed = String(name || "").trim();
  if (!trimmed) {
    errors.push("Name is missing or blank");
  } else if (trimmed.length < MANUAL_NAME_MIN_LENGTH) {
    errors.push("The name field must be at least 2 characters.");
  } else if (trimmed.length > MANUAL_NAME_MAX_LENGTH) {
    errors.push(`Name cannot exceed ${MANUAL_NAME_MAX_LENGTH} characters.`);
  } else if (!nameRegex.test(trimmed)) {
    errors.push("Name can only contain letters, digits, spaces, hyphens, and apostrophes.");
  }
  return errors;
}

/** After removing spaces, keys must work as {{VariableName}} in the workspace (letters, digits, underscore; start with a letter). */
function isValidSmsWorkspaceVariableKey(keyNoSpaces) {
  return /^[A-Za-z][A-Za-z0-9_]*$/.test(String(keyNoSpaces || ""));
}

function validateSmsCsvDynamicColumnKeys(headers) {
  const errors = [];
  const seen = new Set();
  (headers || []).forEach((h) => {
    const trimmedKey = String(h || "").trim();
    if (!trimmedKey) return;
    const keyNoSpaces = trimmedKey.replace(/\s+/g, "");
    if (!keyNoSpaces) return;
    if (keyNoSpaces.toLowerCase() === "name" || keyNoSpaces.toLowerCase() === "phone") return;
    if (!isValidSmsWorkspaceVariableKey(keyNoSpaces)) {
      const msg = `Column "${trimmedKey}" becomes "${keyNoSpaces}" after spaces are removed; use only letters, numbers, and underscores, starting with a letter (e.g. PolicyNumber for {{PolicyNumber}}).`;
      if (!seen.has(msg)) {
        seen.add(msg);
        errors.push(msg);
      }
    }
  });
  return errors;
}

function cleanHeader(h) {
  return String(h || "").trim();
}

function formatUSPhoneInput(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  const a = digits.slice(0, 3);
  const b = digits.slice(3, 6);
  const c = digits.slice(6, 10);
  if (!digits) return "";
  if (digits.length <= 3) return `(${a}`;
  if (digits.length <= 6) return `(${a})${b}`;
  return `(${a})${b}-${c}`;
}

function isValidUSPhoneFormatted(value) {
  return /^\(\d{3}\)\d{3}-\d{4}$/.test(String(value || "").trim());
}

function normalizePhoneToE164(raw) {
  // Lightweight UI-side normalization (server should do the real libphonenumber normalization).
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return { ok: false, e164: "", reason: "Missing phone number" };
  if (digits.length === 10) return { ok: true, e164: `+1${digits}` };
  if (digits.length === 11 && digits[0] === "1") return { ok: true, e164: `+${digits}` };
  if (digits.length >= 8 && digits.length <= 15 && String(raw).trim().startsWith("+")) {
    // Keep as-is for non-US numbers in E.164-ish format
    return { ok: true, e164: `+${digits}` };
  }
  return { ok: false, e164: "", reason: "Invalid phone number" };
}

function validateCSVHeaders(headers, requiredHeaders) {
  const errors = [];
  if (!headers || headers.length === 0) {
    errors.push("CSV has no headers.");
    return errors;
  }
  const cleanHeaders = headers.map((h) => String(h || "").trim().toLowerCase());
  const missing = (requiredHeaders || []).filter(
    (h) => !cleanHeaders.includes(String(h).toLowerCase())
  );
  if (missing.length > 0) {
    errors.push(`CSV is missing required column(s): ${missing.join(", ")}.`);
    return errors;
  }
  return errors;
}

export default function CustomersUploadAndMappingSMS({
  workspace,
  workspaceIntroMessage,
  customers = [],
  onCustomersChange,
  onChange,
  disabled = false,
}) {
  const [showModal, setShowModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  /** Same pattern as CampaignForm CSV modal: string or string[] for row-level errors. */
  const [csvError, setCsvError] = useState("");
  const [csvSuccess, setCsvSuccess] = useState("");
  const [csvProcessing, setCsvProcessing] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualCustomer, setManualCustomer] = useState({ name: "", phone_number: "" });
  const [manualErrors, setManualErrors] = useState({});

  const fileRef = useRef(null);

  // Only clear modal state; do NOT clear customers table (parent state).
  const resetModalOnly = () => {
    setCsvFile(null);
    setCsvError("");
    setCsvSuccess("");
    setCsvProcessing(false);
    if (fileRef.current) fileRef.current.value = null;
  };

  // Explicit "clear customers" action if needed later.
  const clearCustomers = () => {
    if (!onChange) return;
    onChange({
      csv_file: null,
      contact_count: 0,
      invalid_rows_skipped: 0,
      customers: [],
      variable_map: {},
      warnings: [],
      duplicates_found: 0,
      dedupe_mode: "dedupe",
      preview_messages: [],
    });
  };

  const handleCSVUpload = () => {
    if (!csvFile) {
      setCsvError("Please select a CSV file.");
      return;
    }

    const isCSV = csvFile.type === "text/csv" || String(csvFile.name || "").toLowerCase().endsWith(".csv");
    if (!isCSV) {
      setCsvError("Please upload a valid CSV file (.csv).");
      return;
    }

    setCsvError("");
    setCsvSuccess("");
    setCsvProcessing(true);

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => cleanHeader(h),
      complete: (results) => {
        try {
          const headers = (results?.meta?.fields || []).map(cleanHeader).filter(Boolean);
          const parsed = Array.isArray(results?.data) ? results.data : [];

          const headerErrors = validateCSVHeaders(headers, ["name", "phone"]);
          const dynamicKeyErrors = validateSmsCsvDynamicColumnKeys(headers);
          if (headerErrors.length > 0 || dynamicKeyErrors.length > 0) {
            setCsvError([...headerErrors, ...dynamicKeyErrors]);
            setCsvProcessing(false);
            setCsvFile(null);
            if (fileRef.current) fileRef.current.value = null;
            return;
          }
          if (!parsed.length) {
            setCsvError(["CSV file is empty. Please add at least one customer."]);
            setCsvProcessing(false);
            setCsvFile(null);
            if (fileRef.current) fileRef.current.value = null;
            return;
          }

          const getRowValue = (row, key) => {
            const k = Object.keys(row || {}).find(
              (c) => String(c).trim().toLowerCase() === String(key).toLowerCase()
            );
            return k != null ? row[k] : undefined;
          };

          const validEntries = [];
          const invalidEntries = [];

          parsed.forEach((row, index) => {
            const name = String(getRowValue(row, "name") || "").trim();
            const phoneRaw = getRowValue(row, "phone");
            // Match CampaignForm.js CSV import exactly (normalizePhoneNumber + 10-digit check)
            const phone_number = normalizePhoneNumber(phoneRaw);
            const nameErrors = validateCustomerName(name);
            const errors = [...nameErrors];
            if (!phone_number) {
              errors.push("Phone number is missing or blank.");
            } else if (phone_number.length !== 10) {
              errors.push("Phone number must be 10 digits.");
            }

            if (errors.length > 0) {
              invalidEntries.push({
                row: index + 2,
                name: name || "Blank",
                phone_number: phoneRaw != null && String(phoneRaw).trim() !== "" ? String(phoneRaw) : "Blank",
                errors,
              });
              return;
            }

            const phoneE164 = `+1${phone_number}`;

            const dynamic_variables = {};
            Object.keys(row || {}).forEach((key) => {
              const trimmedKey = String(key || "").trim();
              if (!trimmedKey) return;
              const keyNoSpaces = trimmedKey.replace(/\s+/g, "");
              if (!keyNoSpaces) return;
              if (keyNoSpaces.toLowerCase() === "name" || keyNoSpaces.toLowerCase() === "phone") return;
              const value = row[key];
              if (value === undefined || value === null) return;
              const str = String(value).trim();
              if (!str) return;
              dynamic_variables[keyNoSpaces] = str;
            });

            validEntries.push({
              name,
              phone_number: phoneE164,
              ...(Object.keys(dynamic_variables).length ? { dynamic_variables } : {}),
            });
          });

          const baseCustomers = Array.isArray(customers) ? customers : [];
          const mergedCustomers = [...baseCustomers, ...validEntries];
          const invalidCount = invalidEntries.length;

          if (validEntries.length > 0) {
            if (onChange) {
              onChange({
                csv_file: csvFile || null,
                contact_count: mergedCustomers.length,
                invalid_rows_skipped: invalidCount,
                customers: mergedCustomers,
                variable_map: {},
                warnings: [],
                duplicates_found: 0,
                dedupe_mode: "dedupe",
                preview_messages: [],
              });
            }
            if (onCustomersChange) {
              onCustomersChange(mergedCustomers);
            }
          }

          const rowErrorLines = invalidEntries.map(
            (inv) => `Row ${inv.row}: ${inv.errors.join(", ")}`
          );

          if (invalidEntries.length > 0) {
            setCsvError(rowErrorLines);
            setCsvFile(null);
            if (fileRef.current) fileRef.current.value = null;
          } else if (validEntries.length === 0) {
            setCsvError([
              "No valid rows could be imported. Each row needs a name and a valid 10-digit phone number.",
            ]);
            setCsvFile(null);
            if (fileRef.current) fileRef.current.value = null;
          } else {
            setCsvSuccess("All customers processed successfully.");
          }

          const hasRowErrors = invalidEntries.length > 0;
          setCsvProcessing(false);
          if (!hasRowErrors && validEntries.length > 0) {
            setTimeout(() => {
              setShowModal(false);
              resetModalOnly();
            }, 2000);
          }
        } catch (_) {
          setCsvError("Error processing CSV. Please try again.");
          setCsvProcessing(false);
        }
      },
      error: () => {
        setCsvError("Failed to read CSV. Please try again.");
        setCsvProcessing(false);
      },
    });
  };

  // footer controls handle progression

  return (
    <>
      <div className="customer-manager-container" style={{ marginTop: 20 }}>
        <div style={{ marginTop: 6, marginBottom: 12 }}>
          <div
            style={{
              display: "flex",
              gap: 18,
              flexWrap: "wrap",
              fontSize: 14,
              alignItems: "center",
            }}
          >
            <button
              type="button"
              className="btn add-link"
              disabled={disabled}
              style={{ padding: 0, background: "none", fontSize: 14 }}
              onClick={() => setShowManualEntry(true)}
            >
              Add Customer Manually
            </button>

            <button
              type="button"
              className="btn add-link"
              disabled={disabled}
              style={{ padding: 0, background: "none", fontSize: 14 }}
              onClick={() => setShowModal(true)}
            >
              Upload CSV
            </button>

            <CSVSampleDownloadButton
              filename="campaign_customers_sample.csv"
              headers={SAMPLE_HEADERS}
              sampleData={SAMPLE_DATA}
            />
          </div>
        </div>
      </div>

      {showManualEntry && (
        <div className="manual-entry-section mb-4" style={{ margin: "30px 0px 10px 0px" }}>
          <div className="row" style={{ marginBottom: 10 }}>
            <div className="col-md-5">
              <input
                type="text"
                className={`form-control ${manualErrors.name ? "is-invalid" : ""}`}
                placeholder="Name"
                value={manualCustomer.name}
                onChange={(e) => {
                  const v = e.target.value;
                  setManualCustomer((p) => ({ ...p, name: v }));
                  const t = v.trim();
                  if (t.length > MANUAL_NAME_MAX_LENGTH) {
                    setManualErrors((p) => ({
                      ...p,
                      name: `Name cannot exceed ${MANUAL_NAME_MAX_LENGTH} characters.`,
                    }));
                  } else if (manualErrors.name) {
                    setManualErrors((p) => ({ ...p, name: "" }));
                  }
                }}
              />
              {manualErrors.name && (
                <div
                  className="error invalid-feedback d-block"
                  style={{ color: "#dc3545", fontSize: "12px", marginTop: "5px" }}
                >
                  {manualErrors.name}
                </div>
              )}
            </div>

            <div className="col-md-5">
              <input
                type="text"
                className={`form-control ${manualErrors.phone_number ? "is-invalid" : ""}`}
                placeholder="(000)000-0000"
                value={manualCustomer.phone_number}
                onChange={(e) => {
                  const v = formatUSPhoneInput(e.target.value);
                  setManualCustomer((p) => ({ ...p, phone_number: v }));
                  if (manualErrors.phone_number) setManualErrors((p) => ({ ...p, phone_number: "" }));
                }}
              />
              {manualErrors.phone_number && (
                <div
                  className="error invalid-feedback d-block"
                  style={{ color: "#dc3545", fontSize: "12px", marginTop: "5px" }}
                >
                  {manualErrors.phone_number}
                </div>
              )}
            </div>

            <div className="col-md-1">
              <button
                type="button"
                className="btn add-link"
                title="Add"
                style={{ fontSize: "14px" }}
                onClick={() => {
                  const nextErr = {};
                  const name = String(manualCustomer.name || "").trim();
                  const phone = String(manualCustomer.phone_number || "").trim();
                  if (!name) nextErr.name = "Name is required.";
                  else {
                    const nameErrors = validateCustomerName(name);
                    if (nameErrors.length > 0) nextErr.name = nameErrors[0];
                  }
                  if (!phone) {
                    nextErr.phone_number = "Phone number is required.";
                  } else if (!isValidUSPhoneFormatted(phone)) {
                    nextErr.phone_number = "Phone number must be in the format (123)456-7890.";
                  }
                  const norm = normalizePhoneToE164(phone);
                  if (!nextErr.phone_number && !norm.ok) nextErr.phone_number = "Phone number is invalid.";
                  if (Object.keys(nextErr).length) {
                    setManualErrors(nextErr);
                    return;
                  }
                  const newRow = { name, phone_number: norm.e164, manual: true };
                  const nextCustomers = [...(Array.isArray(customers) ? customers : []), newRow];
                  if (onCustomersChange) onCustomersChange(nextCustomers);
                  if (onChange) {
                    onChange({
                      csv_file: null,
                      contact_count: nextCustomers.length,
                      invalid_rows_skipped: 0,
                      customers: nextCustomers,
                      variable_map: {},
                      warnings: [],
                      duplicates_found: 0,
                      dedupe_mode: "dedupe",
                      preview_messages: [],
                    });
                  }
                  // Match Voice behavior: keep inputs visible, just reset values.
                  setManualCustomer({ name: "", phone_number: "" });
                  setManualErrors({});
                }}
              >
                +Add
              </button>
            </div>
          </div>
        </div>
      )}

      <ModalSMS
        show={showModal}
        maxWidth="600px"
        title="Upload Customers"
        onClose={() => {
          setShowModal(false);
          resetModalOnly();
        }}
        body={
          <div className="compaign-form-detail">
            <div style={{ fontSize: "12px", color: "#6c757d", marginBottom: 12, lineHeight: 1.4 }}>
              Spaces in CSV column names are removed (e.g. &quot;Policy Number&quot; → PolicyNumber). Use{" "}
              <code style={{ background: "#f1f3f4", padding: "1px 4px", borderRadius: 3 }}>
                {"{{"}VariableName{"}}"}
              </code>{" "}
              in your workspace to insert the value during SMS.
            </div>

            {csvSuccess && (
              <Message color="green" style={{ display: csvSuccess ? "block" : "none", marginTop: 30 }}>
                {csvSuccess}
              </Message>
            )}

            {csvError && (
              <div style={{ color: "red", marginBottom: 10 }}>
                {Array.isArray(csvError) ? (
                  <div>
                    <ul style={{ margin: 0, maxHeight: 200, overflowY: "auto", paddingLeft: 20 }}>
                      {csvError.map((err, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>
                          {typeof err === "string" ? err : JSON.stringify(err)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <span>{csvError}</span>
                )}
              </div>
            )}

            {csvProcessing && <div className="ui loading form"></div>}
            <input
              type="file"
              accept=".csv"
              ref={fileRef}
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  setCsvFile(e.target.files[0]);
                  setCsvError("");
                  setCsvSuccess("");
                }
              }}
              className="form-control my-3"
              disabled={disabled || csvProcessing}
            />
          </div>
        }
        footer={
          <div className="modal-footer d-flex justify-content-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                resetModalOnly();
              }}
              className="yr-cancelbtn linear-gradient-cancel btn-sm yr-btn-small"
            >
              <span>Cancel</span>
            </button>
            <button
                  type="button"
                  onClick={() => {
                    handleCSVUpload();
                  }}
                  disabled={
                    csvProcessing ||
                    !csvFile ||
                    !!csvSuccess ||
                    (Array.isArray(csvError) ? csvError.length > 0 : !!csvError)
                  }
                  className="btn linear-gradient yr-submitbtn btn-sm"
                >
                  {csvProcessing ? "Processing..." : "Save"}
            </button>
          </div>
        }
      />
    </>
  );
}

